/**
 * Billing-specific constants.
 * Plan tiers and payment codes are in @scio3/shared-types/subscription.
 * This file has UI-specific constants only.
 */

/** Add-on display names */
export const ADDON_DISPLAY_NAMES: Record<string, string> = {
  aericast: 'Wireless Presentation',
  videowall: 'Video Wall',
  backgroundmusic: 'OptiSound Music',
  engage: 'Engage',
} as const;

/** Add-on default prices (USD monthly, used as fallback) */
export const ADDON_DEFAULT_PRICES: Record<string, number> = {
  aericast: 20,
  videowall: 25,
  backgroundmusic: 15,
} as const;

/**
 * Sold as a base package that already includes the 3 licenses, plus a per-screen
 * price beyond that; credits scale with the license count. Mirrors
 * `productPlans.add_ons.optidev` (`minQuantity: 3`, `numberScreenTrial: 3`) — do
 * not change these without the backend. Credits exist only in OptiDev and never
 * in the OptiSigns catalogue, so they are constants here; they refresh MONTHLY on
 * every interval, which is why no credits string interpolates the interval.
 */
export const OPTIDEV_MIN_LICENSES = 3;
export const OPTIDEV_BASE_CREDITS = 100;
export const OPTIDEV_CREDITS_PER_EXTRA_SCREEN = 40;

/** Billing interval display labels */
export const BILLING_INTERVAL_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  yearly: 'Yearly',
  yearly_freedevice: 'Yearly (Free Device)',
} as const;

/**
 * Tier catalog for multi-tier add-ons. Keys the price lookup in
 * `productPlans.add_ons.{addonKey}.price.{currency}.{priceKey}.price`.
 *
 * Only add-ons present in this map render the Standard/Pro radio cards;
 * single-tier add-ons (aericast, videowall) continue to render the flat
 * quantity-only UI.
 */
export const ADDON_TIER_CATALOG: Record<
  string,
  Array<{ tier: 'standard' | 'pro'; priceKeyMonthly: string; priceKeyYearly: string }>
> = {
  backgroundmusic: [
    { tier: 'standard', priceKeyMonthly: 'monthly', priceKeyYearly: 'yearly' },
    { tier: 'pro', priceKeyMonthly: 'pro_monthly', priceKeyYearly: 'pro_yearly' },
  ],
} as const;

/** Feature bullet i18n keys per add-on tier, rendered as-is in tier cards.
 *  Copy matches scio1 `subscriptionPlanModal` for OptiSound so the tier-selection
 *  dialog stays on-brand with the existing product-marketing language.
 */
export const ADDON_TIER_FEATURE_KEYS: Record<string, Record<'standard' | 'pro', string[]>> = {
  backgroundmusic: {
    standard: [
      'Licensed and ad-free music stations, curated for commercial use',
      'Offline music playback',
      'Simultaneous audio playback with digital signage',
    ],
    pro: [
      'Includes all features from the Standard plan',
      'Unlimited access to premium stations featuring top-charting artists',
    ],
  },
} as const;

/** Invoice status display config — matches scio1 status mapping */
export const INVOICE_STATUS_CONFIG = {
  paid: {
    label: 'Successfully Charged',
    variant: 'success' as const,
    icon: 'CheckCircle' as const,
  },
  open: { label: 'Failed Charge', variant: 'destructive' as const, icon: 'XCircle' as const },
  upcoming: { label: 'Upcoming Charge', variant: 'secondary' as const, icon: 'Calendar' as const },
  draft: { label: 'Draft', variant: 'secondary' as const, icon: 'CheckCircle' as const },
  void: { label: 'Void', variant: 'secondary' as const, icon: 'CheckCircle' as const },
  uncollectible: {
    label: 'Uncollectible',
    variant: 'secondary' as const,
    icon: 'CheckCircle' as const,
  },
} as const;

/** Page size for invoice pagination */
export const INVOICE_PAGE_SIZE = 10;
export const INVOICE_PAGE_SIZE_SUB = 5;

/**
 * Stripe-supported tax ID types (from scio1 COUNTRY_TAXS).
 * Format: code → "CODE - Country" display label.
 * Ref: https://stripe.com/docs/invoicing/customer/tax-ids
 */
export const TAX_ID_TYPES: { code: string; label: string }[] = [
  { code: 'ae_trn', label: 'AE TRN - United Arab Emirates' },
  { code: 'au_abn', label: 'AU ABN - Australia' },
  { code: 'au_arn', label: 'AU ARN - Australia' },
  { code: 'bg_uic', label: 'BG UIC - Bulgaria' },
  { code: 'br_cnpj', label: 'BR CNPJ - Brazil' },
  { code: 'br_cpf', label: 'BR CPF - Brazil' },
  { code: 'ca_bn', label: 'CA BN - Canada' },
  { code: 'ca_gst_hst', label: 'CA GST HST - Canada' },
  { code: 'ca_pst_bc', label: 'CA PST BC - Canada' },
  { code: 'ca_pst_mb', label: 'CA PST MB - Canada' },
  { code: 'ca_pst_sk', label: 'CA PST SK - Canada' },
  { code: 'ca_qst', label: 'CA QST - Canada' },
  { code: 'ch_vat', label: 'CH VAT - Switzerland' },
  { code: 'cl_tin', label: 'CL TIN - Chile' },
  { code: 'eg_tin', label: 'EG TIN - Egypt' },
  { code: 'es_cif', label: 'ES CIF - Spain' },
  { code: 'eu_oss_vat', label: 'EU OSS VAT - European Union' },
  { code: 'eu_vat', label: 'EU VAT - European Union' },
  { code: 'gb_vat', label: 'GB VAT - United Kingdom' },
  { code: 'ge_vat', label: 'GE VAT - Georgia' },
  { code: 'hk_br', label: 'HK BR - Hong Kong' },
  { code: 'hu_tin', label: 'HU TIN - Hungary' },
  { code: 'id_npwp', label: 'ID NPWP - Indonesia' },
  { code: 'il_vat', label: 'IL VAT - Israel' },
  { code: 'in_gst', label: 'IN GST - India' },
  { code: 'is_vat', label: 'IS VAT - Iceland' },
  { code: 'jp_cn', label: 'JP CN - Japan' },
  { code: 'jp_rn', label: 'JP RN - Japan' },
  { code: 'jp_trn', label: 'JP TRN - Japan' },
  { code: 'ke_pin', label: 'KE PIN - Kenya' },
  { code: 'kr_brn', label: 'KR BRN - South Korea' },
  { code: 'li_uid', label: 'LI UID - Liechtenstein' },
  { code: 'mx_rfc', label: 'MX RFC - Mexico' },
  { code: 'my_frp', label: 'MY FRP - Malaysia' },
  { code: 'my_itn', label: 'MY ITN - Malaysia' },
  { code: 'my_sst', label: 'MY SST - Malaysia' },
  { code: 'no_vat', label: 'NO VAT - Norway' },
  { code: 'nz_gst', label: 'NZ GST - New Zealand' },
  { code: 'ph_tin', label: 'PH TIN - Philippines' },
  { code: 'ru_inn', label: 'RU INN - Russia' },
  { code: 'ru_kpp', label: 'RU KPP - Russia' },
  { code: 'sa_vat', label: 'SA VAT - Saudi Arabia' },
  { code: 'sg_gst', label: 'SG GST - Singapore' },
  { code: 'sg_uen', label: 'SG UEN - Singapore' },
  { code: 'si_tin', label: 'SI TIN - Slovenia' },
  { code: 'th_vat', label: 'TH VAT - Thailand' },
  { code: 'tr_tin', label: 'TR TIN - Turkey' },
  { code: 'tw_vat', label: 'TW VAT - Taiwan' },
  { code: 'ua_vat', label: 'UA VAT - Ukraine' },
  { code: 'us_ein', label: 'US EIN - United States' },
  { code: 'za_vat', label: 'ZA VAT - South Africa' },
];

/** Sample tax ID values per type — used as input placeholders (from scio1 COUNTRY_TAXS_SAMPLE) */
export const TAX_ID_SAMPLES: Record<string, string> = {
  ae_trn: '123456789012345',
  au_abn: '12345678912',
  au_arn: '123456789123',
  bg_uic: '123456789',
  br_cnpj: '01.234.456/5432-10',
  br_cpf: '123.456.789-87',
  ca_bn: '123456789',
  ca_gst_hst: '123456789RT0002',
  ca_pst_bc: 'PST-1234-5678',
  ca_pst_mb: '123456-7',
  ca_pst_sk: '1234567',
  ca_qst: '1234567890TQ1234',
  ch_vat: 'CHE-123.456.789 MWST',
  cl_tin: '12.345.678-K',
  eg_tin: '123456789',
  es_cif: 'A12345678',
  eu_oss_vat: 'EU123456789',
  eu_vat: 'XI123456789',
  gb_vat: 'GB123456789',
  ge_vat: '123456789',
  hk_br: '12345678',
  hu_tin: '12345678-1-23',
  id_npwp: '12.345.678.9-012.345',
  il_vat: '000012345',
  in_gst: '12ABCDE3456FGZH',
  is_vat: '123456',
  jp_cn: '1234567891234',
  jp_rn: '12345',
  jp_trn: 'T1234567891234',
  ke_pin: 'P000111111A',
  kr_brn: '123-45-67890',
  li_uid: 'CHE123456789',
  mx_rfc: 'ABC010203AB9',
  my_frp: '12345678',
  my_itn: 'C 1234567890',
  my_sst: 'A12-3456-78912345',
  no_vat: '123456789MVA',
  nz_gst: '123456789',
  ph_tin: '123456789012',
  ru_inn: '1234567891',
  ru_kpp: '123456789',
  sa_vat: '123456789012345',
  sg_gst: 'M12345678X',
  sg_uen: '123456789F',
  si_tin: '12345678',
  th_vat: '1234567891234',
  tr_tin: '0123456789',
  tw_vat: '12345678',
  ua_vat: '123456789',
  us_ein: '12-3456789',
  za_vat: '4123456789',
};

/**
 * Free Plan device support — scio1 parity (#1140).
 *
 * scio1 source of truth: `imports/api/companies/collection.js:41-47`
 *   `FILTER_FREE_PLAN_DEVICE_NOT_SUPPORT` is an `$or` MongoDB query that
 *   matches the devices a Free-plan account is NOT allowed to keep paired.
 *
 *   [
 *     { os: { $in: ['iPadOS', 'tvOS', 'iOS'] } },
 *     { device: { $in: ['webplayer', 'roku', 'tizen', 'samsung'] }, os: { $ne: 'android' } },
 *     { model: 'Chromecast', os: 'android' },
 *     { typeBuild: 'amazon' },
 *     { manufacturer: 'Amazon' },
 *   ]
 *
 * The "supported" list shown in the user-facing message ("OptiSigns, Win,
 * Mac, Linux, RPi") is the inverse — anything NOT matching the conditions
 * above is supported.
 */
export const FREE_PLAN_MAX_DEVICES = 3;

/**
 * SP53 — backend payment status code for "Free plan device limit exceeded".
 * Mirrors `optisigns-open-api/src/app/shared/common-defs.ts` PAYMENT_STATUS_CODE.LIMIT_QUANTITY.
 * Returned by `downgradeTrialToFreePlan` / `downgradePaidToFreePlan` when the account
 * has more screens than Free allows OR has unsupported device types (Roku, Firestick,
 * Tizen, iOS, etc.). Triggers the ScreenManagementDialog handoff so the user can
 * remove or move offending devices inline.
 */
export const PAYMENT_STATUS_CODE_LIMIT_QUANTITY = 5555;

export type FreePlanDeviceShape = {
  os?: string | null;
  device?: string | null;
  model?: string | null;
  typeBuild?: string | null;
  manufacturer?: string | null;
};

export function isUnsupportedFreePlanDevice(d: FreePlanDeviceShape): boolean {
  return getUnsupportedFreePlanReason(d) !== null;
}

/**
 * Returns a short, human-readable device-type label explaining WHY the device
 * is incompatible with the Free plan (e.g. "Roku", "Amazon Fire TV", "iOS").
 * Returns null if the device is supported. The label is the device family the
 * row failed on — matches the rules in `isUnsupportedFreePlanDevice` /
 * scio1's `FILTER_FREE_PLAN_DEVICE_NOT_SUPPORT`.
 *
 * Brand/product names are kept as-is across locales (i18n only the trailing
 * "not supported on Free" copy in the dialog row, not these labels).
 */
export function getUnsupportedFreePlanReason(d: FreePlanDeviceShape): string | null {
  const os = (d.os ?? '').toLowerCase();
  const device = (d.device ?? '').toLowerCase();
  const model = d.model ?? '';
  const typeBuild = (d.typeBuild ?? '').toLowerCase();
  const manufacturer = (d.manufacturer ?? '').toLowerCase();

  if (os === 'ios') return 'iOS';
  if (os === 'ipados') return 'iPadOS';
  if (os === 'tvos') return 'Apple TV';
  if (device === 'roku' && os !== 'android') return 'Roku';
  if (device === 'tizen' && os !== 'android') return 'Tizen';
  if (device === 'samsung' && os !== 'android') return 'Samsung';
  if (device === 'webplayer' && os !== 'android') return 'Web Player';
  if (model === 'Chromecast' && os === 'android') return 'Chromecast';
  if (typeBuild === 'amazon' || manufacturer === 'amazon') return 'Amazon Fire TV';
  return null;
}
